import { useRef, useState, useCallback, useEffect } from 'react'
import { Square, Pencil, MousePointer2, Eraser, Undo2, Trash2, ZoomIn, ZoomOut } from 'lucide-react'

export type AnnotationTool = 'rectangle' | 'lasso' | 'brush' | 'eraser'

export interface ImageAnnotationToolProps {
  imageUrl: string
  /** Fallback URL if imageUrl fails to load */
  imageUrlFallback?: string | null
  onMaskChange?: (maskDataUrl: string | null) => void
  className?: string
  /** When set, draws this PNG data URL onto the canvas as the initial mask */
  initialMaskDataUrl?: string | null
}

interface Point {
  x: number
  y: number
}

const MIN_ZOOM = 0.5
const MAX_ZOOM = 4
const ZOOM_STEP = 0.25

export function ImageAnnotationTool({
  imageUrl,
  imageUrlFallback,
  onMaskChange,
  className = '',
  initialMaskDataUrl,
}: ImageAnnotationToolProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  const [tool, setTool] = useState<AnnotationTool>('brush')
  const [brushSize, setBrushSize] = useState(16)
  const [isDrawing, setIsDrawing] = useState(false)
  const [points, setPoints] = useState<Point[]>([])
  const [startPoint, setStartPoint] = useState<Point | null>(null)
  const [canvasSize, setCanvasSize] = useState<{ w: number; h: number } | null>(null)
  const [zoom, setZoom] = useState(1)
  /** Undo history — stack of PNG data URLs (before each committed stroke) */
  const undoStackRef = useRef<string[]>([])

  // ─── Coordinate helpers ──────────────────────────────────────────────────

  const getCanvasPoint = useCallback(
    (clientX: number, clientY: number): Point | null => {
      const canvas = canvasRef.current
      if (!canvas) return null
      const rect = canvas.getBoundingClientRect()
      const scaleX = canvas.width / rect.width
      const scaleY = canvas.height / rect.height
      return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY,
      }
    },
    []
  )

  // ─── Canvas drawing helpers ───────────────────────────────────────────────

  const prepareCtxForTool = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      if (tool === 'eraser') {
        ctx.globalCompositeOperation = 'destination-out'
        ctx.fillStyle = 'rgba(0,0,0,1)'
        ctx.strokeStyle = 'rgba(0,0,0,1)'
      } else {
        ctx.globalCompositeOperation = 'source-over'
        ctx.fillStyle = 'rgba(255, 255, 255, 1)'
        ctx.strokeStyle = 'rgba(255, 255, 255, 1)'
      }
      ctx.lineWidth = brushSize
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
    },
    [tool, brushSize]
  )

  /** Save current canvas state to the undo stack */
  const pushUndo = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    undoStackRef.current = [...undoStackRef.current.slice(-19), canvas.toDataURL('image/png')]
  }, [])

  const emitMask = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    // Check if canvas has any non-transparent pixels
    const pixel = ctx.getImageData(0, 0, canvas.width, canvas.height).data
    const hasContent = pixel.some((v, i) => i % 4 === 3 && v > 0)
    onMaskChange?.(hasContent ? canvas.toDataURL('image/png') : null)
  }, [onMaskChange])

  const commitToMask = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || !canvasSize) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    prepareCtxForTool(ctx)
    if (tool === 'rectangle' && startPoint && points.length > 0) {
      const p = points[points.length - 1]
      const x = Math.min(startPoint.x, p.x)
      const y = Math.min(startPoint.y, p.y)
      const w = Math.abs(p.x - startPoint.x)
      const h = Math.abs(p.y - startPoint.y)
      ctx.fillRect(x, y, w, h)
    } else if (tool === 'lasso' && points.length > 1) {
      ctx.beginPath()
      ctx.moveTo(points[0].x, points[0].y)
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x, points[i].y)
      }
      ctx.closePath()
      ctx.fill()
    }
    ctx.globalCompositeOperation = 'source-over'
    setPoints([])
    setStartPoint(null)
    emitMask()
  }, [tool, startPoint, points, canvasSize, prepareCtxForTool, emitMask])

  // ─── Size observer ────────────────────────────────────────────────────────

  useEffect(() => {
    const container = containerRef.current
    const img = imageRef.current
    if (!container || !img) return
    const updateSize = () => {
      const w = img.clientWidth
      const h = img.clientHeight
      if (w > 0 && h > 0) setCanvasSize({ w, h })
    }
    updateSize()
    const ro = new ResizeObserver(updateSize)
    ro.observe(container)
    return () => ro.disconnect()
  }, [imageUrl])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !canvasSize) return
    const dpr = window.devicePixelRatio || 1
    canvas.width = canvasSize.w * dpr
    canvas.height = canvasSize.h * dpr
    canvas.style.width = `${canvasSize.w}px`
    canvas.style.height = `${canvasSize.h}px`
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.scale(dpr, dpr)
      ctx.clearRect(0, 0, canvasSize.w, canvasSize.h)
    }
    undoStackRef.current = []
    onMaskChange?.(null)
  }, [canvasSize, imageUrl])

  // ─── Load initial mask from external data URL ─────────────────────────────

  useEffect(() => {
    if (!initialMaskDataUrl || !canvasSize) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const img = new Image()
    img.onload = () => {
      const dpr = window.devicePixelRatio || 1
      ctx.save()
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.scale(dpr, dpr)
      ctx.drawImage(img, 0, 0, canvasSize.w, canvasSize.h)
      ctx.restore()
      emitMask()
    }
    img.src = initialMaskDataUrl
  }, [initialMaskDataUrl, canvasSize, emitMask])

  // ─── Zoom ────────────────────────────────────────────────────────────────

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault()
      setZoom((prev) => {
        const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP
        return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.round((prev + delta) * 100) / 100))
      })
    }
    el.addEventListener('wheel', handleWheel, { passive: false })
    return () => el.removeEventListener('wheel', handleWheel)
  }, [])

  // ─── Mouse handlers ───────────────────────────────────────────────────────

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return
      const p = getCanvasPoint(e.clientX, e.clientY)
      if (!p) return
      pushUndo()
      setIsDrawing(true)
      setStartPoint(p)
      setPoints([p])
      if (tool === 'brush' || tool === 'eraser') {
        const canvas = canvasRef.current
        if (!canvas || !canvasSize) return
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        prepareCtxForTool(ctx)
        ctx.beginPath()
        ctx.arc(p.x, p.y, brushSize / 2, 0, Math.PI * 2)
        ctx.fill()
        ctx.globalCompositeOperation = 'source-over'
        emitMask()
      }
    },
    [getCanvasPoint, tool, canvasSize, brushSize, prepareCtxForTool, emitMask, pushUndo]
  )

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const p = getCanvasPoint(e.clientX, e.clientY)
      if (!p || !isDrawing) return
      if (tool === 'brush' || tool === 'eraser') {
        const canvas = canvasRef.current
        if (!canvas || !canvasSize) return
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        prepareCtxForTool(ctx)
        ctx.beginPath()
        ctx.arc(p.x, p.y, brushSize / 2, 0, Math.PI * 2)
        ctx.fill()
        ctx.globalCompositeOperation = 'source-over'
        emitMask()
      } else {
        setPoints((prev) => [...prev, p])
      }
    },
    [getCanvasPoint, isDrawing, tool, canvasSize, brushSize, prepareCtxForTool, emitMask]
  )

  const handleMouseUp = useCallback(() => {
    if (!isDrawing) return
    if (tool === 'rectangle' || tool === 'lasso') {
      commitToMask()
    }
    setIsDrawing(false)
  }, [isDrawing, tool, commitToMask])

  const handleMouseLeave = useCallback(() => {
    if (isDrawing && (tool === 'rectangle' || tool === 'lasso')) {
      commitToMask()
    }
    setIsDrawing(false)
  }, [isDrawing, tool, commitToMask])

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp)
    window.addEventListener('mouseleave', handleMouseLeave)
    return () => {
      window.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [handleMouseUp, handleMouseLeave])

  // ─── Touch handlers ───────────────────────────────────────────────────────

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault()
      const touch = e.touches[0]
      if (!touch) return
      const p = getCanvasPoint(touch.clientX, touch.clientY)
      if (!p) return
      pushUndo()
      setIsDrawing(true)
      setStartPoint(p)
      setPoints([p])
      if (tool === 'brush' || tool === 'eraser') {
        const canvas = canvasRef.current
        if (!canvas || !canvasSize) return
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        prepareCtxForTool(ctx)
        ctx.beginPath()
        ctx.arc(p.x, p.y, brushSize / 2, 0, Math.PI * 2)
        ctx.fill()
        ctx.globalCompositeOperation = 'source-over'
        emitMask()
      }
    },
    [getCanvasPoint, tool, canvasSize, brushSize, prepareCtxForTool, emitMask, pushUndo]
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault()
      const touch = e.touches[0]
      if (!touch || !isDrawing) return
      const p = getCanvasPoint(touch.clientX, touch.clientY)
      if (!p) return
      if (tool === 'brush' || tool === 'eraser') {
        const canvas = canvasRef.current
        if (!canvas || !canvasSize) return
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        prepareCtxForTool(ctx)
        ctx.beginPath()
        ctx.arc(p.x, p.y, brushSize / 2, 0, Math.PI * 2)
        ctx.fill()
        ctx.globalCompositeOperation = 'source-over'
        emitMask()
      } else {
        setPoints((prev) => [...prev, p])
      }
    },
    [getCanvasPoint, isDrawing, tool, canvasSize, brushSize, prepareCtxForTool, emitMask]
  )

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault()
      if (!isDrawing) return
      if (tool === 'rectangle' || tool === 'lasso') {
        commitToMask()
      }
      setIsDrawing(false)
    },
    [isDrawing, tool, commitToMask]
  )

  // ─── Undo ─────────────────────────────────────────────────────────────────

  const handleUndo = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || !canvasSize) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const stack = undoStackRef.current
    if (stack.length === 0) return
    const prev = stack[stack.length - 1]
    undoStackRef.current = stack.slice(0, -1)
    const img = new Image()
    img.onload = () => {
      const dpr = window.devicePixelRatio || 1
      ctx.save()
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.scale(dpr, dpr)
      ctx.drawImage(img, 0, 0, canvasSize.w, canvasSize.h)
      ctx.restore()
      emitMask()
    }
    img.src = prev
  }, [canvasSize, emitMask])

  // ─── Clear ────────────────────────────────────────────────────────────────

  const handleClear = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || !canvasSize) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    pushUndo()
    ctx.clearRect(0, 0, canvasSize.w, canvasSize.h)
    setPoints([])
    setStartPoint(null)
    onMaskChange?.(null)
  }, [canvasSize, onMaskChange, pushUndo])

  // ─── Preview overlay (rectangle/lasso) ───────────────────────────────────

  const renderPreview = () => {
    if (!canvasSize || points.length === 0) return null
    if (tool === 'rectangle' && startPoint && points.length > 0) {
      const p = points[points.length - 1]
      const x = Math.min(startPoint.x, p.x)
      const y = Math.min(startPoint.y, p.y)
      const w = Math.abs(p.x - startPoint.x)
      const h = Math.abs(p.y - startPoint.y)
      return (
        <div
          className="absolute border-2 border-white border-dashed pointer-events-none"
          style={{ left: x, top: y, width: w, height: h }}
        />
      )
    }
    return null
  }

  const canUndo = undoStackRef.current.length > 0

  return (
    <div className={className}>
      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-sm font-medium text-slate-600">Herramienta:</span>
        <div className="flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
          <button
            type="button"
            title="Rectángulo"
            onClick={() => setTool('rectangle')}
            className={`p-2 rounded-md transition-colors ${tool === 'rectangle' ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <Square className="w-4 h-4" />
          </button>
          <button
            type="button"
            title="Lazo libre"
            onClick={() => setTool('lasso')}
            className={`p-2 rounded-md transition-colors ${tool === 'lasso' ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <MousePointer2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            title="Pincel"
            onClick={() => setTool('brush')}
            className={`p-2 rounded-md transition-colors ${tool === 'brush' ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            type="button"
            title="Borrador parcial"
            onClick={() => setTool('eraser')}
            className={`p-2 rounded-md transition-colors ${tool === 'eraser' ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <Eraser className="w-4 h-4" />
          </button>
        </div>

        {(tool === 'brush' || tool === 'lasso' || tool === 'eraser') && (
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-500">Tamaño</label>
            <input
              type="range"
              min={4}
              max={48}
              value={brushSize}
              onChange={(e) => setBrushSize(Number(e.target.value))}
              className="w-24 h-2"
            />
            <span className="text-xs text-slate-400">{brushSize}px</span>
          </div>
        )}

        {/* Zoom controls */}
        <div className="flex items-center gap-1 ml-auto">
          <button
            type="button"
            onClick={() => setZoom((z) => Math.max(MIN_ZOOM, Math.round((z - ZOOM_STEP) * 100) / 100))}
            disabled={zoom <= MIN_ZOOM}
            className="p-1.5 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-40 transition-colors"
            title="Alejar"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-xs text-slate-500 w-10 text-center">{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            onClick={() => setZoom((z) => Math.min(MAX_ZOOM, Math.round((z + ZOOM_STEP) * 100) / 100))}
            disabled={zoom >= MAX_ZOOM}
            className="p-1.5 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-40 transition-colors"
            title="Acercar"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Secondary toolbar — undo / clear */}
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={handleUndo}
          disabled={!canUndo}
          className="flex items-center gap-1.5 px-2 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-md transition-colors disabled:opacity-40"
          title="Deshacer último trazo"
        >
          <Undo2 className="w-4 h-4" />
          Deshacer
        </button>
        <button
          type="button"
          onClick={handleClear}
          className="flex items-center gap-1.5 px-2 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
          title="Borrar máscara completa"
        >
          <Trash2 className="w-4 h-4" />
          Borrar todo
        </button>
      </div>

      {/* Canvas container with zoom */}
      <div
        ref={containerRef}
        className="overflow-auto rounded-lg bg-slate-100"
        style={{ maxHeight: '40vh' }}
      >
        <div
          style={{
            transform: `scale(${zoom})`,
            transformOrigin: 'top left',
            display: 'inline-block',
          }}
        >
          <div className="relative inline-block bg-slate-100 rounded-lg overflow-hidden">
            <img
              ref={imageRef}
              src={imageUrl}
              alt="Imagen a anotar"
              className="block max-w-full object-contain select-none"
              draggable={false}
              onLoad={() => {
                if (imageRef.current && containerRef.current) {
                  const w = imageRef.current.clientWidth
                  const h = imageRef.current.clientHeight
                  if (w > 0 && h > 0) setCanvasSize({ w, h })
                }
              }}
              onError={(e) => {
                if (imageUrlFallback) {
                  ;(e.target as HTMLImageElement).src = imageUrlFallback
                }
              }}
            />
            {canvasSize && (
              <>
                <canvas
                  ref={canvasRef}
                  className="absolute top-0 left-0 cursor-crosshair"
                  style={{
                    width: canvasSize.w,
                    height: canvasSize.h,
                    opacity: 0.6,
                  }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseLeave}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                />
                {renderPreview()}
              </>
            )}
          </div>
        </div>
      </div>

      <p className="text-xs text-slate-500 mt-1.5">
        Rectángulo: arrastrar. Lazo: dibujar contorno. Pincel: pintar. Borrador: borrar parcialmente. Rueda del ratón para hacer zoom.
      </p>
    </div>
  )
}
